import { useState, useEffect } from 'react';
import { formatVND, todayInputValue } from '../../../../utils/format';
import { showToast } from '../../../../components/base/Toast';
import { useModalBackHandler } from '../../../../hooks/useModalBackHandler';
import { useCategories } from '../../../../hooks/useCategories';
import { MATERIAL_GROUP_FALLBACK, UNIT_OF_MEASURE_FALLBACK } from '../../../../constants/categoryFallbacks';
import { getInputSuppliersByHtx } from '../../../../store';
import type { SupplyItem } from '../../../../types';

interface SupplyFormModalProps {
  open: boolean;
  item: SupplyItem | null;
  htxId?: string;
  onClose: () => void;
  onSave: (data: Omit<SupplyItem, 'id'>) => void | Promise<void>;
}

export function SupplyFormModal({ open, item, htxId, onClose, onSave }: SupplyFormModalProps) {
  useModalBackHandler(open, onClose);
  const isEdit = !!item;
  const { names: NHOM_LIST, addCategory, creating: addingGroup } = useCategories('MATERIAL_GROUP', MATERIAL_GROUP_FALLBACK);
  const { names: unitOptions } = useCategories('UNIT_OF_MEASURE', UNIT_OF_MEASURE_FALLBACK);
  const suppliers = getInputSuppliersByHtx(htxId);
  const [addingGroupOpen, setAddingGroupOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [form, setForm] = useState({
    maVatTu: '',
    name: '',
    nhom: '',
    unit: 'kg',
    stock: '',
    available: '',
    giaNhap: '',
    giaCungUng: '',
    nhaCungCap: '',
    supplierId: '',
    loHang: '',
    hanSuDung: '',
    ngưỡngCảnhBáo: '',
    ghiChu: '',
    status: 'Đủ' as SupplyItem['status'],
  });

  useEffect(() => {
    if (item) {
      setForm({
        maVatTu: item.maVatTu,
        name: item.name,
        nhom: item.nhom,
        unit: item.unit,
        stock: String(item.stock),
        available: String(item.available),
        giaNhap: item.giaNhap ? String(item.giaNhap) : '',
        giaCungUng: item.giaCungUng ? String(item.giaCungUng) : '',
        nhaCungCap: item.nhaCungCap || '',
        supplierId: item.supplierId || '',
        loHang: item.loHang || '',
        hanSuDung: item.hanSuDung || '',
        ngưỡngCảnhBáo: item.ngưỡngCảnhBáo ? String(item.ngưỡngCảnhBáo) : '',
        ghiChu: item.ghiChu || '',
        status: item.status,
      });
    } else {
      setForm({
        maVatTu: `VT-HTX-${String(Date.now()).slice(-3)}`,
        name: '',
        nhom: '',
        unit: 'kg',
        stock: '',
        available: '',
        giaNhap: '',
        giaCungUng: '',
        nhaCungCap: '',
        supplierId: '',
        loHang: '',
        hanSuDung: '',
        ngưỡngCảnhBáo: '',
        ghiChu: '',
        status: 'Đủ',
      });
    }
  }, [item, open]);

  useEffect(() => {
    if (!form.nhom && NHOM_LIST.length > 0) setForm((f) => ({ ...f, nhom: NHOM_LIST[0] }));
  }, [NHOM_LIST, form.nhom]);

  const handleSave = async () => {
    if (!form.maVatTu.trim() || !form.name.trim() || !form.unit.trim()) {
      showToast('Vui lòng điền mã, tên và đơn vị', 'warning');
      return;
    }
    const stock = Number(form.stock) || 0;
    const available = Number(form.available) || 0;
    // Only meaningful when creating — on an edit both fields are read-only and
    // simply echo the item, since quantities move through Nhập/Xuất/Điều chỉnh.
    if (!isEdit && available > stock) {
      showToast('Số lượng khả dụng không được lớn hơn tồn kho', 'warning');
      return;
    }

    let status: SupplyItem['status'] = 'Đủ';
    const ngưỡng = Number(form.ngưỡngCảnhBáo) || 0;
    if (stock <= 0) status = 'Hết hàng';
    else if (ngưỡng > 0 && stock <= ngưỡng) status = 'Sắp hết';

    // The caller owns closing and the success/failure toast — it is the one that
    // knows whether the request actually landed, so a failed save keeps the form
    // (and everything typed into it) on screen.
    setSaving(true);
    try {
      await onSave({
        maVatTu: form.maVatTu.trim(),
        name: form.name.trim(),
        nhom: form.nhom,
        stock,
        available,
        reserved: 0,
        unit: form.unit.trim(),
        giaNhap: form.giaNhap ? Number(form.giaNhap) : undefined,
        giaCungUng: form.giaCungUng ? Number(form.giaCungUng) : undefined,
        nhaCungCap: form.nhaCungCap || undefined,
        supplierId: form.supplierId || undefined,
        loHang: form.loHang || undefined,
        hanSuDung: form.hanSuDung || undefined,
        status,
        ngưỡngCảnhBáo: ngưỡng || undefined,
        ghiChu: form.ghiChu || undefined,
      });
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/40 animate-modal-backdrop" onClick={onClose} />
      <div onClick={(e) => e.stopPropagation()} className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-lg max-h-[100dvh] sm:max-h-[90vh] overflow-y-auto animate-modal-slide-up">
        <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900">{isEdit ? 'Sửa vật tư' : 'Thêm vật tư mới'}</h3>
          <button type="button" onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400">
            <i className="ri-close-line text-lg" />
          </button>
        </div>
        <div className="px-6 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Mã vật tư</label>
              <input type="text" value={form.maVatTu} onChange={(e) => setForm({ ...form, maVatTu: e.target.value })} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" placeholder="VD: VT-HTX-001" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Nhóm</label>
              <select value={form.nhom} onChange={(e) => setForm({ ...form, nhom: e.target.value })} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500">
                {NHOM_LIST.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              {!addingGroupOpen ? (
                <button type="button" onClick={() => setAddingGroupOpen(true)} className="mt-1 text-xs font-medium text-agrida-700 hover:text-agrida-800">
                  + Thêm nhóm mới
                </button>
              ) : (
                <div className="mt-1.5 flex gap-1.5">
                  <input
                    autoFocus
                    type="text"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    placeholder="Tên nhóm mới"
                    className="flex-1 px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-agrida-500"
                  />
                  <button
                    type="button"
                    disabled={addingGroup || !newGroupName.trim()}
                    onClick={async () => {
                      const created = await addCategory(newGroupName);
                      if (created) {
                        setForm((f) => ({ ...f, nhom: created.name }));
                        setNewGroupName('');
                        setAddingGroupOpen(false);
                      }
                    }}
                    className="px-2.5 py-1.5 rounded-lg text-xs font-medium text-white bg-agrida-700 hover:bg-agrida-800 disabled:opacity-50 whitespace-nowrap"
                  >
                    Lưu
                  </button>
                  <button type="button" onClick={() => { setAddingGroupOpen(false); setNewGroupName(''); }} className="px-2 py-1.5 rounded-lg text-xs text-slate-500 hover:bg-slate-100">
                    Hủy
                  </button>
                </div>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Tên vật tư <span className="text-red-500">*</span></label>
            <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" placeholder="Tên vật tư" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Đơn vị <span className="text-red-500">*</span></label>
              <input type="text" list="supply-unit-options" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" placeholder="kg, lít, cái..." />
              <datalist id="supply-unit-options">
                {unitOptions.map((u) => (<option key={u} value={u} />))}
              </datalist>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Số lượng tồn</label>
              <input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} disabled={isEdit} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500 disabled:bg-slate-50 disabled:text-slate-400" placeholder="0" />
            </div>
          </div>
          {isEdit && (
            <p className="text-xs text-sky-600 -mt-2">
              Tồn kho và khả dụng chỉ thay đổi qua Nhập kho / Xuất kho / Điều chỉnh — để mỗi lần thay đổi đều có lý do trong lịch sử.
            </p>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Khả dụng</label>
              <input type="number" value={form.available} onChange={(e) => setForm({ ...form, available: e.target.value })} disabled={isEdit} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500 disabled:bg-slate-50 disabled:text-slate-400" placeholder="0" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Ngưỡng cảnh báo</label>
              <input type="number" value={form.ngưỡngCảnhBáo} onChange={(e) => setForm({ ...form, ngưỡngCảnhBáo: e.target.value })} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" placeholder="0" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Giá nhập</label>
              <div className="relative">
                <input type="number" value={form.giaNhap} onChange={(e) => setForm({ ...form, giaNhap: e.target.value })} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500 pr-12" placeholder="0" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">VND</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Giá cung ứng</label>
              <div className="relative">
                <input type="number" value={form.giaCungUng} onChange={(e) => setForm({ ...form, giaCungUng: e.target.value })} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500 pr-12" placeholder="0" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">VND</span>
              </div>
            </div>
          </div>
          {form.giaNhap && form.giaCungUng && (
            <div className="bg-sky-50 rounded-xl p-3 text-sm">
              <p className="text-sky-700">Chênh lệch giá: {formatVND(Number(form.giaCungUng) - Number(form.giaNhap))} ({(((Number(form.giaCungUng) - Number(form.giaNhap)) / Number(form.giaNhap)) * 100).toFixed(1)}%)</p>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Nhà cung cấp</label>
            {suppliers.length > 0 && (
              <select
                value={form.supplierId}
                onChange={(e) => {
                  const supplierId = e.target.value;
                  const picked = suppliers.find((s) => s.id === supplierId);
                  setForm({ ...form, supplierId, nhaCungCap: picked ? picked.name : form.nhaCungCap });
                }}
                className="w-full mb-1.5 px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
              >
                <option value="">— Chọn từ danh sách nhà cung cấp —</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            )}
            <input
              type="text"
              value={form.nhaCungCap}
              onChange={(e) => setForm({ ...form, nhaCungCap: e.target.value, supplierId: '' })}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
              placeholder="Hoặc nhập tên nhà cung cấp khác"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Lô hàng</label>
              <input type="text" value={form.loHang} onChange={(e) => setForm({ ...form, loHang: e.target.value })} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" placeholder="Lô hàng" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Hạn sử dụng</label>
              <input type="date" value={form.hanSuDung} onChange={(e) => setForm({ ...form, hanSuDung: e.target.value })} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Ghi chú</label>
            <textarea value={form.ghiChu} onChange={(e) => setForm({ ...form, ghiChu: e.target.value })} maxLength={500} rows={2} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" placeholder="Ghi chú..." />
          </div>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-slate-100 px-6 py-4 flex gap-3 justify-end">
          <button type="button" onClick={onClose} disabled={saving} className="px-4 py-2.5 rounded-xl text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 transition-colors whitespace-nowrap min-h-[44px]">Hủy</button>
          <button type="button" onClick={handleSave} disabled={saving} className="px-4 py-2.5 rounded-xl text-sm font-medium text-white bg-agrida-700 hover:bg-agrida-800 disabled:opacity-50 transition-colors whitespace-nowrap min-h-[44px]">{saving ? 'Đang lưu...' : isEdit ? 'Lưu thay đổi' : 'Thêm mới'}</button>
        </div>
      </div>
    </div>
  );
}